import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Save, X, Send, MessageSquare, User, Calendar, Mail, Phone } from 'lucide-react';
import { useAuth } from '@/contexts/auth';
import { usePermissions } from '@/hooks/use-permissions';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useContactSubmissions, ContactSubmission, ContactResponse } from '@/hooks/admin/contact/useContactSubmissions';
import { useContactCategories } from '@/hooks/admin/contact/useContactCategories';
import ContactAttachmentsSection from '@/components/admin/contact/ContactAttachmentsSection';
import { format } from 'date-fns';

const AdminContactDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isLoading } = useAuth();
  const { isSuperAdmin, isAdmin } = usePermissions();
  const { updateSubmission, getResponses, addResponse } = useContactSubmissions();
  const { categories } = useContactCategories();
  
  const [submission, setSubmission] = useState<ContactSubmission | null>(null);
  const [responses, setResponses] = useState<ContactResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    status: '',
    assigned_to: '',
    priority: ''
  });
  
  // Response form state
  const [responseText, setResponseText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [sendingResponse, setSendingResponse] = useState(false);

  useEffect(() => {
    document.title = "Contact Details - NationCard";
    
    if (!isLoading) {
      if (!user) {
        toast.error("Please log in to access the admin dashboard");
        navigate("/admin");
        return;
      }
      
      if (!isAdmin() && !isSuperAdmin()) {
        toast.error("You do not have permission to access the admin dashboard");
        navigate("/");
        return;
      }
    }
  }, [user, isAdmin, isSuperAdmin, navigate, isLoading]);

  const fetchSubmissionDetails = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      
      // Fetch submission details
      const { data: submissionData, error: submissionError } = await supabase
        .from('contact_submissions')
        .select(`
          *,
          contact_categories(name),
          profiles(name, email)
        `)
        .eq('id', id)
        .single();
      
      if (submissionError) throw submissionError;
      
      setSubmission(submissionData);
      setFormData({
        status: submissionData.status,
        assigned_to: submissionData.assigned_to || '',
        priority: submissionData.priority
      });
      
      // Fetch responses
      const responsesData = await getResponses(id);
      setResponses(responsesData);
      
    } catch (error) {
      console.error('Error fetching submission details:', error);
      toast.error('Failed to load submission details');
      navigate('/admin/contact');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubmissionDetails();
  }, [id]);

  const handleFormChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!submission || !hasChanges) return;
    
    try {
      setSaving(true);
      await updateSubmission(submission.id, {
        status: formData.status as any,
        assigned_to: formData.assigned_to || undefined,
        priority: formData.priority as any
      });
      
      // Update local state
      setSubmission(prev => prev ? { 
        ...prev, 
        status: formData.status as any,
        assigned_to: formData.assigned_to || undefined,
        priority: formData.priority as any
      } : null);
      setHasChanges(false);
      toast.success('Changes saved successfully');
    } catch (error) {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!submission) return;
    
    setFormData({
      status: submission.status,
      assigned_to: submission.assigned_to || '',
      priority: submission.priority
    });
    setHasChanges(false);
  };

  const handleAddResponse = async () => {
    if (!submission || !responseText.trim()) return;
    
    try {
      setSendingResponse(true);
      await addResponse(submission.id, {
        response_message: responseText,
        is_internal_note: isInternalNote
      });
      
      // Refresh responses
      const updatedResponses = await getResponses(submission.id);
      setResponses(updatedResponses);
      setResponseText('');
      setIsInternalNote(false);
      toast.success('Response added successfully');
    } catch (error) {
      // Error handled in hook
    } finally {
      setSendingResponse(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'urgent': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-lg">Submission not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="border-b border-border bg-background">
        <div className="flex h-16 items-center px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/contact')}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Contact Management
            </Button>
            <div className="h-6 w-px bg-border" />
            <div>
              <h1 className="font-semibold">Contact Submission #{submission.id.slice(-8)}</h1>
              <p className="text-sm text-muted-foreground">{submission.subject}</p>
            </div>
          </div>
          
          <div className="ml-auto flex items-center gap-2">
            {hasChanges && (
              <>
                <Button variant="outline" onClick={handleCancel} disabled={saving}>
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-medium">Name</Label>
                    <div className="text-lg">{submission.name}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Email</Label>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <a href={`mailto:${submission.email}`} className="text-blue-600 hover:underline">
                        {submission.email}
                      </a>
                    </div>
                  </div>
                  {submission.phone && (
                    <div>
                      <Label className="text-sm font-medium">Phone</Label>
                      <div className="flex items-center gap-2">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        <a href={`tel:${submission.phone}`} className="text-blue-600 hover:underline">
                          {submission.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label className="text-sm font-medium">Submitted</Label>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      {format(new Date(submission.created_at), 'PPP p')}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Message */}
            <Card>
              <CardHeader>
                <CardTitle>Message</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div>
                    <Label className="text-sm font-medium">Subject</Label>
                    <div className="text-lg font-medium">{submission.subject}</div>
                  </div>
                  <div>
                    <Label className="text-sm font-medium">Message</Label>
                    <div className="mt-2 p-4 bg-muted rounded-md whitespace-pre-wrap">
                      {submission.message}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Attachments */}
            <ContactAttachmentsSection attachments={typeof submission.attachments === 'string' ? submission.attachments : JSON.stringify(submission.attachments)} />

            {/* Responses */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Responses & Communication
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="responses" className="w-full">
                  <TabsList>
                    <TabsTrigger value="responses">Response History</TabsTrigger>
                    <TabsTrigger value="add-response">Add Response</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="responses" className="space-y-4 mt-4">
                    {responses.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No responses yet
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {responses.map((response) => (
                          <div key={response.id} className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium">
                                  {response.profiles?.name?.charAt(0) || 'A'}
                                </div>
                                <div>
                                  <div className="font-medium">{response.profiles?.name || 'Admin'}</div>
                                  <div className="text-sm text-muted-foreground">
                                    {format(new Date(response.created_at), 'PPP p')}
                                  </div>
                                </div>
                                {response.is_internal_note && (
                                  <Badge variant="secondary">Internal Note</Badge>
                                )}
                              </div>
                            </div>
                            <div className="bg-muted p-3 rounded-md whitespace-pre-wrap">
                              {response.response_message}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="add-response" className="mt-4">
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="response">Response Message</Label>
                        <Textarea
                          id="response"
                          value={responseText}
                          onChange={(e) => setResponseText(e.target.value)}
                          rows={6}
                          placeholder="Type your response..."
                          className="mt-1"
                        />
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="internal-note"
                          checked={isInternalNote}
                          onChange={(e) => setIsInternalNote(e.target.checked)}
                          className="rounded"
                        />
                        <Label htmlFor="internal-note">Internal note (not visible to customer)</Label>
                      </div>
                      
                      <Button 
                        onClick={handleAddResponse} 
                        disabled={!responseText.trim() || sendingResponse}
                        className="w-full"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {sendingResponse ? 'Sending...' : (isInternalNote ? 'Add Internal Note' : 'Send Response')}
                      </Button>
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status Management */}
            <Card>
              <CardHeader>
                <CardTitle>Status & Assignment</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => handleFormChange('status', value)}
                  >
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="resolved">Resolved</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="priority">Priority</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(value) => handleFormChange('priority', value)}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Quick Info */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Info</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-sm font-medium">Category</Label>
                  <div>{submission.contact_categories?.name || 'Uncategorized'}</div>
                </div>
                <div>
                  <Label className="text-sm font-medium">Current Status</Label>
                  <Badge className={getStatusColor(submission.status)}>
                    {submission.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div>
                  <Label className="text-sm font-medium">Priority Level</Label>
                  <Badge className={getPriorityColor(submission.priority)}>
                    {submission.priority}
                  </Badge>
                </div>
                {submission.resolved_at && (
                  <div>
                    <Label className="text-sm font-medium">Resolved At</Label>
                    <div className="text-sm">{format(new Date(submission.resolved_at), 'PPP p')}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminContactDetail;